import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  BlogArticleDto,
  BlogArticleShortDto,
  fetchBlogArticle,
  fetchBlogArticles,
} from './useApi';

const PAGE_SIZE = 10;

function getArticleId(article: Partial<BlogArticleDto> | Partial<BlogArticleShortDto>): string {
  return (article as any).blogArticleID ?? (article as any).blogArticleId ?? '';
}

function getArticleCreatedDate(article: Partial<BlogArticleDto> | Partial<BlogArticleShortDto>): string {
  return (article as any).createdDate ?? '';
}

const Articles = () => {
  const [articles, setArticles] = useState<BlogArticleShortDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [keywordInput, setKeywordInput] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('');
  const [pageIndex, setPageIndex] = useState(1);

  const [selectedArticle, setSelectedArticle] = useState<BlogArticleDto | null>(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [articleError, setArticleError] = useState<string | null>(null);

  const hasMore = useMemo(() => articles.length < totalCount, [articles.length, totalCount]);

  const loadArticles = async (requestedPageIndex: number, keyword: string, append: boolean) => {
    setLoadingList(true);
    setListError(null);

    try {
      const response = await fetchBlogArticles(PAGE_SIZE, requestedPageIndex, keyword);
      setTotalCount(response.totalCount ?? 0);
      setArticles((current) => (append ? [...current, ...(response.data ?? [])] : response.data ?? []));
      setPageIndex(requestedPageIndex);
    } catch (error: any) {
      setListError(error.message || 'Failed to load articles.');
      if (!append) {
        setArticles([]);
        setTotalCount(0);
      }
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadArticles(1, '', false);
  }, []);

  const handleSearch = async () => {
    const trimmedKeyword = keywordInput.trim();
    setActiveKeyword(trimmedKeyword);
    setSelectedArticle(null);
    await loadArticles(1, trimmedKeyword, false);
  };

  const handleLoadMore = async () => {
    const nextPageIndex = pageIndex + 1;
    await loadArticles(nextPageIndex, activeKeyword, true);
  };

  const handleOpenArticle = async (articleId: string) => {
    setLoadingArticle(true);
    setArticleError(null);

    try {
      const article = await fetchBlogArticle(articleId);
      setSelectedArticle(article);
    } catch (error: any) {
      setArticleError(error.message || 'Failed to load article.');
    } finally {
      setLoadingArticle(false);
    }
  };

  return (
    <Box className="f4k-page articles-page">
      <Box className="f4k-panel articles-panel" sx={{ width: 'min(1200px, 100%)' }}>
        <Typography variant="h4" className="f4k-title" sx={{ mb: 2 }}>
          Articles
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Keyword"
            value={keywordInput}
            onChange={(event) => setKeywordInput(event.target.value)}
            size="small"
            sx={{ minWidth: '240px' }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loadingList}
            sx={{
              backgroundColor: 'var(--f4k-brand-600)',
              '&:hover': { backgroundColor: 'var(--f4k-brand-700)' },
            }}
          >
            Search
          </Button>
        </Box>

        {listError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {listError}
          </Alert>
        )}

        {selectedArticle ? (
          <Box sx={{ mb: 3 }}>
            <Button variant="text" onClick={() => setSelectedArticle(null)} sx={{ mb: 2 }}>
              Back to results
            </Button>
            <Typography variant="h5" sx={{ mb: 1, textAlign: 'left' }}>
              {selectedArticle.title}
            </Typography>
            {!!selectedArticle.author && (
              <Typography variant="body2" sx={{ mb: 2, textAlign: 'left', color: 'var(--f4k-text-600)' }}>
                {selectedArticle.author}
              </Typography>
            )}
            <Box
              sx={{
                textAlign: 'left',
                lineHeight: 1.8,
                '& p': { marginTop: 0, marginBottom: 2 },
                '& h1, & h2, & h3': { color: 'var(--f4k-brand-700)' },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {selectedArticle.content || ''}
              </ReactMarkdown>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            {loadingList && articles.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={26} />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {articles.map((article, index) => {
                  const articleId = getArticleId(article);

                  return (
                    <Card key={`${articleId}-${index}`} variant="outlined">
                      <CardContent sx={{ textAlign: 'left' }}>
                        <Button
                          variant="text"
                          onClick={() => handleOpenArticle(articleId)}
                          sx={{ padding: 0, textTransform: 'none', fontSize: '1.05rem', fontWeight: 700 }}
                        >
                          {article.title}
                        </Button>
                        <Typography variant="body2" sx={{ mt: 1, color: 'var(--f4k-text-600)' }}>
                          {article.summary}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                          {getArticleCreatedDate(article)
                            ? new Date(getArticleCreatedDate(article)).toLocaleDateString()
                            : ''}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}

                {!loadingList && articles.length === 0 && (
                  <Typography variant="body1" sx={{ textAlign: 'left', py: 2 }}>
                    No articles found.
                  </Typography>
                )}
              </Stack>
            )}

            {loadingArticle && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {articleError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {articleError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleLoadMore}
                disabled={!hasMore || loadingList}
                sx={{
                  backgroundColor: 'var(--f4k-brand-600)',
                  '&:hover': { backgroundColor: 'var(--f4k-brand-700)' },
                }}
              >
                {loadingList ? 'Loading...' : hasMore ? 'Load next 10' : 'No more articles'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Articles;
